import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import { Box, Chip, Typography } from '@material-ui/core'

import { ClaimCard } from './components'

import constants from 'constants/index'
import useStyles from './styles'

const ClaimsAnalysis = ({
  claims,
  decisions,
  specialistFindings,
  derivedFindings,
}) => {
  const { CLAIM_STATUS_FILTERS, CLAIM_STATUS_FILTER_VALUES, CLAIM_TYPE_LABEL } =
    constants.dai.disputes

  const classes = useStyles()
  const [expanded, setExpanded] = useState({})
  const [statusFilter, setStatusFilter] = useState(
    CLAIM_STATUS_FILTER_VALUES.ALL,
  )
  const [typeFilter, setTypeFilter] = useState(CLAIM_STATUS_FILTER_VALUES.ALL)

  const toggle = (claimId) =>
    setExpanded((previous) => ({ ...previous, [claimId]: !previous[claimId] }))

  const safeDecisions = decisions || {}

  const claimTypes = useMemo(() => {
    if (isEmpty(claims)) return []
    const unique = new Set(
      claims.map((claim) => claim.primary_type).filter(Boolean),
    )
    return Array.from(unique)
  }, [claims])

  const filteredClaims = useMemo(() => {
    if (isEmpty(claims)) return []
    return claims.filter((claim) => {
      const decision = safeDecisions[claim.claim_id] || {}
      const isAdmissible = decision.is_admissible
      if (
        statusFilter === CLAIM_STATUS_FILTER_VALUES.ADMISSIBLE &&
        !isAdmissible
      )
        return false
      if (
        statusFilter === CLAIM_STATUS_FILTER_VALUES.GROUNDLESS &&
        isAdmissible
      )
        return false
      if (
        typeFilter !== CLAIM_STATUS_FILTER_VALUES.ALL &&
        claim.primary_type !== typeFilter
      )
        return false
      return true
    })
  }, [claims, safeDecisions, statusFilter, typeFilter])

  if (isEmpty(claims)) return null

  const counters = claims.reduce(
    (accumulator, claim) => {
      const decision = safeDecisions[claim.claim_id] || {}
      if (decision.is_admissible) accumulator.admissible += 1
      else accumulator.groundless += 1
      return accumulator
    },
    { admissible: 0, groundless: 0 },
  )

  return (
    <Box>
      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Análise dos pontos do auditor
      </Typography>

      <Box className={classes.toolbar}>
        <Typography className={classes.toolbarLabel}>Status</Typography>
        {CLAIM_STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter.value}
            size="small"
            label={filter.label}
            onClick={() =>
              setStatusFilter(
                statusFilter === filter.value &&
                  filter.value !== CLAIM_STATUS_FILTER_VALUES.ALL
                  ? CLAIM_STATUS_FILTER_VALUES.ALL
                  : filter.value,
              )
            }
            className={clsx(classes.toolbarChip, {
              [classes.toolbarChipActive]: statusFilter === filter.value,
            })}
          />
        ))}

        {!isEmpty(claimTypes) && (
          <>
            <Box className={classes.toolbarDivider} />
            <Typography className={classes.toolbarLabel}>Tipo</Typography>
            <Chip
              size="small"
              label="Todos"
              onClick={() => setTypeFilter(CLAIM_STATUS_FILTER_VALUES.ALL)}
              className={clsx(classes.toolbarChip, {
                [classes.toolbarChipActive]:
                  typeFilter === CLAIM_STATUS_FILTER_VALUES.ALL,
              })}
            />
            {claimTypes.map((type) => (
              <Chip
                key={type}
                size="small"
                label={CLAIM_TYPE_LABEL[type] || type}
                onClick={() =>
                  setTypeFilter(
                    typeFilter === type ? CLAIM_STATUS_FILTER_VALUES.ALL : type,
                  )
                }
                className={clsx(classes.toolbarChip, {
                  [classes.toolbarChipActive]: typeFilter === type,
                })}
              />
            ))}
          </>
        )}

        <Typography className={classes.toolbarSummary}>
          {`${filteredClaims.length} de ${claims.length} · ${counters.admissible} com fundamento · ${counters.groundless} sem fundamento`}
        </Typography>
      </Box>

      {isEmpty(filteredClaims) ? (
        <Box className={classes.empty}>
          <Typography variant="body2" color="textSecondary">
            Nenhum ponto atende aos filtros selecionados.
          </Typography>
        </Box>
      ) : (
        <Box className={classes.list}>
          {filteredClaims.map((claim) => (
            <ClaimCard
              key={claim.claim_id}
              claim={claim}
              decision={decisions[claim.claim_id] || {}}
              findings={specialistFindings?.[claim.claim_id] || []}
              overlays={derivedFindings?.[claim.claim_id] || []}
              isOpen={!!expanded[claim.claim_id]}
              onToggle={() => toggle(claim.claim_id)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

ClaimsAnalysis.propTypes = {
  claims: PropTypes.array,
  decisions: PropTypes.object,
  specialistFindings: PropTypes.object,
  derivedFindings: PropTypes.object,
}

ClaimsAnalysis.defaultProps = {
  claims: [],
  decisions: {},
  specialistFindings: {},
  derivedFindings: {},
}

export default ClaimsAnalysis
